import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PublicNavbar from '../components/PublicNavbar';
import PublicFooter from '../components/PublicFooter';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { useFaqContent, useSectionContent } from '../hooks/useSiteContent';

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={onClick}
        className="w-full py-5 flex items-start justify-between text-left hover:bg-gray-50 transition-colors"
        aria-expanded={isOpen}
      >
        <span className="text-base font-medium text-gray-900 pr-8">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="pb-5 pr-12">
          <p className="text-gray-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

// Default FAQ items (BlueLock Freights brand; CMS overrides at runtime)
const DEFAULT_FAQS = [
  {
    question: "What types of drivers does BlueLock Freights work with?",
    answer: "BlueLock Freights works with professional Owner-Operators, Lease-On Drivers, and Company Drivers operating under structured agreements with BlueLock Transport LLC."
  },
  {
    question: "Do you offer OTR and Regional opportunities?",
    answer: "Yes. Opportunities vary based on freight availability and operational needs."
  },
  {
    question: "How does the settlement process work?",
    answer: "Compensation is program-based and outlined in individual operating agreements. Settlements are issued weekly. No income is guaranteed."
  },
  {
    question: "Is income guaranteed?",
    answer: "No. Compensation is based on dispatched freight and contract terms. No income guarantees are made."
  },
  {
    question: "What is the onboarding process?",
    answer: "Approved drivers complete a structured onboarding process including documentation review and contract execution prior to dispatch."
  },
  {
    question: "How long does onboarding take?",
    answer: "Timelines vary depending on document completion and verification."
  }
];

// Default header content
const FAQ_HEADER_DEFAULTS = {
  faq_hero_title: "Driver Frequently Asked Questions",
  faq_hero_subtitle: "Common questions about driving with BlueLock Freights",
  faq_cta_title: "Have More Questions?",
  faq_cta_description: "Submit your application and our team will be in touch."
};

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const { faqs } = useFaqContent(DEFAULT_FAQS);
  const { content } = useSectionContent('faq', FAQ_HEADER_DEFAULTS);

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="faq-page">
      <PublicNavbar />

      {/* Header */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {content.faq_hero_title}
          </h1>
          <p className="text-lg text-blue-100">
            {content.faq_hero_subtitle}
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === index}
                onClick={() => handleToggle(index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Program Comparison Section */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Program Comparison
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="faq-program-comparison-table">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 border-b">Feature</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 border-b">Owner-Operator</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 border-b">Lease-On Driver</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 border-b">Company Driver</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-600">Operating Authority</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Driver's Own Authority</td>
                  <td className="px-6 py-4 text-sm text-gray-900">BlueLock Transport LLC</td>
                  <td className="px-6 py-4 text-sm text-gray-900">BlueLock Transport LLC</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-600">Compensation Type</td>
                  <td className="px-6 py-4 text-sm text-gray-900">8% gross or $400/wk per truck</td>
                  <td className="px-6 py-4 text-sm text-gray-900">84% / 16% linehaul split</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Per company driver agreement</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-600">Settlement Schedule</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Weekly</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Weekly</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Per agreement</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-600">Dispatch Support</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Company-Coordinated</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Company-Coordinated</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Company-Coordinated</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-600">Equipment Ownership</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Driver-Owned</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Driver-Owned</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Company-Provided</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-600">Contract Type</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Dispatch Agreement</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Lease-On Agreement</td>
                  <td className="px-6 py-4 text-sm text-gray-900">Company Driver Agreement</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-center text-sm text-gray-500 mt-6">
            All terms are defined within individual operating agreements.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            {content.faq_cta_title}
          </h2>
          <p className="text-blue-100 mb-8">
            {content.faq_cta_description}
          </p>
          <Link
            to="/apply"
            data-testid="faq-apply-btn"
            className="inline-flex items-center justify-center px-8 py-3 bg-white text-blue-900 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
          >
            Apply Now
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default FAQPage;
